<script>
export default {
  name: 'ExpandableGroupTable',
  props: {
    /**
     * Array de grupos. Cada grupo deve ter:
     * - key (String|Number): identificador único do grupo
     * - title (String): título exibido no header
     * - items (Array): itens da tabela dentro do grupo
     * - subtitle (String, opcional): texto secundário à direita (ex: "3 ativo(s)")
     * - value (String, opcional): valor à direita (ex: "R$ 10.000,00")
     */
    groups: {
      type: Array,
      default: () => [],
    },
    /**
     * Headers da BaseTable (mesmo formato do v-data-table)
     */
    headers: {
      type: Array,
      default: () => [],
    },
    /**
     * Chaves dos grupos que começam expandidos
     */
    expandedKeys: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      internalExpanded: [...this.expandedKeys],
    }
  },
  watch: {
    expandedKeys(val) {
      this.internalExpanded = [...val]
    },
  },
  methods: {
    isExpanded(key) {
      return this.internalExpanded.includes(key)
    },
    toggleExpand(key) {
      if (this.isExpanded(key)) {
        this.internalExpanded = this.internalExpanded.filter((k) => k !== key)
      } else {
        this.internalExpanded = [...this.internalExpanded, key]
      }
      this.$emit('toggle', { key, expanded: this.isExpanded(key) })
    },
  },
}
</script>

<template>
  <div>
    <div v-for="group in groups" :key="group.key" class="mb-3">
      <!-- GROUP HEADER -->
      <div
        class="expandable-group-header d-flex align-center pa-3 mb-2"
        role="button"
        tabindex="0"
        :aria-expanded="String(isExpanded(group.key))"
        @click="toggleExpand(group.key)"
        @keydown.enter="toggleExpand(group.key)"
        @keydown.space.prevent="toggleExpand(group.key)"
      >
        <icon-svg
          :name="isExpanded(group.key) ? 'mdi-chevron-up' : 'mdi-chevron-down'"
          size="20"
          class="mr-2"
        />
        <span class="text-heading-small-medium text-color-900">
          {{ group.title }}
        </span>

        <v-spacer />

        <!-- Slot para conteúdo customizado à direita do header -->
        <slot
          name="header-right"
          :group="group"
          :expanded="isExpanded(group.key)"
        >
          <span v-if="group.subtitle" class="az-text-small mr-4">
            {{ group.subtitle }}
          </span>
          <span v-if="group.value" class="text-heading-small-medium text-color-900">
            {{ group.value }}
          </span>
        </slot>
      </div>

      <!-- EXPANDED CONTENT -->
      <v-expand-transition>
        <div v-if="isExpanded(group.key)">
          <!-- Slot para conteúdo customizado do grupo expandido -->
          <slot
            name="content"
            :group="group"
            :headers="headers"
            :items="group.items"
          >
            <!-- Fallback: BaseTable padrão -->
            <BaseTable
              disableMarginTop
              :headers="headers"
              :items="group.items || []"
              hide-default-footer
              :items-per-page="(group.items || []).length || 10"
              dense
              no-data-text="Sem registros."
            />
          </slot>
        </div>
      </v-expand-transition>
    </div>
  </div>
</template>

<style lang="sass" scoped>
.expandable-group-header
  background-color: #f5f5f5
  border-radius: 8px
  cursor: pointer
  transition: background-color 200ms ease
  &:hover
    background-color: #eeeeee

</style>
